<script setup lang="ts">
  import { ref, computed, watch, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { Copy, ExternalLink } from "lucide-vue-next";
  import { useClipboard } from "@vueuse/core";
  import { useSurveyShare, type SurveyShareForm } from "@utils/composables/surveyShare";
  import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Tooltip } from "@/components/ui/tooltip";
  import QrCodeAction from "@/components/Survey/Share/QrCodeAction.vue";
  import type { Survey } from "@/types";

  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  const surveyLocalesStore = useSurveyLocalesStore();
  const { localeOptions } = storeToRefs(surveyLocalesStore);

  const props = defineProps<{
    survey: Survey;
  }>();
  const form = ref<SurveyShareForm>({
    country: "",
    language: "",
    page: "",
    testLink: "",
    liveLink: "",
  });
  const { constructUrl, initForm, targetCountryOptions, languageId, hasCustomPages, pageOptions, fetchSurveyChatPages } =
    useSurveyShare(props.survey, form.value, localeOptions);

  const links = [
    {
      kind: "test" as const,
      field: "testLink" as const,
      title: "Test link",
      label: "Test",
      description: "",
    },
    {
      kind: "live" as const,
      field: "liveLink" as const,
      title: "Live link",
      label: "Live",
      description: "Only collects responses once the survey status is set to Live.",
    },
  ];

  const { copy, copied } = useClipboard();
  const copiedKind = ref<string>("");
  const copyLink = (kind: string, url: string) => {
    copiedKind.value = kind;
    copy(url);
  };
  const isCopied = (kind: string) => copied.value && copiedKind.value === kind;

  // e.g. episto-qr-FR-fr-live — explicit filename including country, language and type
  const qrFilename = (type: "test" | "live"): string =>
    `episto-qr-${form.value.country}-${languageId.value}-${type}`;

  const gridColumns = computed(() => (hasCustomPages.value ? "md:grid-cols-3" : "md:grid-cols-2"));

  // Default values when the component is mounted
  onMounted(() => {
    initForm();
    fetchSurveyChatPages();
  });

  watch(
    () => [form.value.country, form.value.language, form.value.page],
    ([country, language]) => {
      form.value.testLink = constructUrl(country, language, "test");
      form.value.liveLink = constructUrl(country, language, "live");
    },
  );
</script>

<template>
  <Card class="w-full p-10">
    <CardHeader class="gap-1 p-0 pb-8">
      <CardTitle class="text-2xl font-bold text-primary">Links</CardTitle>
      <CardDescription>One link per country and language. Pick them below.</CardDescription>
    </CardHeader>
    <CardContent class="space-y-8 p-0">
      <div
        class="grid gap-5"
        :class="gridColumns">
        <div class="space-y-2">
          <Label>Country</Label>
          <Select
            :model-value="form.country"
            :options="targetCountryOptions"
            data-cy="country-select"
            filterable
            placeholder="Select"
            @update:model-value="(value) => (form.country = value as string)" />
        </div>
        <div class="space-y-2">
          <Label>Language</Label>
          <Select
            :model-value="form.language"
            :options="localeOptions"
            data-cy="language-select"
            filterable
            placeholder="Select"
            @update:model-value="(value) => (form.language = value as string)" />
        </div>
        <div
          v-if="hasCustomPages"
          class="space-y-2">
          <Label>Page</Label>
          <Select
            :model-value="form.page"
            :options="pageOptions"
            data-cy="page-select"
            placeholder="Episto"
            @update:model-value="(value) => (form.page = value as string)" />
        </div>
      </div>

      <div class="space-y-4">
        <section
          v-for="link in links"
          :key="link.kind"
          class="rounded-xl border p-5">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-primary">{{ link.title }}</h3>
              <p
                v-if="link.description"
                class="mt-1 text-13 text-muted-foreground">
                {{ link.description }}
              </p>
            </div>
            <QrCodeAction
              :url="form[link.field]"
              :label="link.label"
              :filename="qrFilename(link.kind)" />
          </div>

          <div class="mt-4 flex items-center gap-1 rounded-lg border bg-muted py-1.5 pr-1.5 pl-3">
            <input
              v-model="form[link.field]"
              :data-cy="`${link.kind}-link-input`"
              class="min-w-0 flex-1 bg-transparent font-mono text-13 text-foreground outline-none"
              spellcheck="false" />
            <Tooltip content="Open in a new tab">
              <Button
                as="a"
                variant="ghost"
                size="icon"
                class="text-muted-foreground hover:text-primary"
                :href="form[link.field]"
                target="_blank"
                rel="noopener">
                <ExternalLink class="size-4" />
              </Button>
            </Tooltip>
            <Button
              :data-cy="`copy-${link.kind}-link`"
              @click="copyLink(link.kind, form[link.field])">
              <Copy class="size-4" />
              {{ isCopied(link.kind) ? "Copied!" : "Copy" }}
            </Button>
          </div>
        </section>
      </div>
    </CardContent>
  </Card>
</template>
